import React, { useState, useEffect } from "react";
import axios from "axios";
import { useTheme } from "@mui/material/styles";
import {
    Box,
    CircularProgress,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Button,
    TextField,
    Grid,
    MenuItem,
} from "@mui/material";

/**
 * EditCheck Component (Meal Report)
 *
 * - Fetches vw_MealSummary on mount and populates "site" dropdown from it.
 * - Aggregates by School/Date/MealService, bucketing counts by MealStatus.
 * - Filters by month, site, and meal service.
 * - Adapts to light/dark mode with alternating row colors.
 */
const EditCheck = () => {
    const theme = useTheme();

    // raw view data
    const [mealData, setMealData] = useState([]);
    // aggregated groups after clicking "Generate Report"
    const [aggregatedData, setAggregatedData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // filters
    const [filterMonth, setFilterMonth] = useState("");
    const [filterSite, setFilterSite] = useState("");
    const [filterMealService, setFilterMealService] = useState("");

    // --- Fetch view data once on mount ---
    useEffect(() => {
        (async () => {
            try {
                const resp = await axios.get(
                    "https://figazuresql.swancreate.com/api/swa/vw_MealSummary"
                );
                setMealData(resp.data || []);
            } catch {
                setError("Error loading meal summary.");
            }
        })();
    }, []);

    // --- Generate aggregated report ---
    const handleGenerateReport = () => {
        setLoading(true);
        setError(null);
        try {
            const agg = {};
            mealData.forEach((rec) => {
                const school = rec.SchoolName?.trim() || "Unknown";
                const d = new Date(rec.ServiceDate);
                const yyyy = d.getFullYear(),
                    mm = String(d.getMonth() + 1).padStart(2, "0"),
                    dd = String(d.getDate()).padStart(2, "0");
                const dateStr = `${yyyy}-${mm}-${dd}`;
                const svc = rec.MealService || "";
                const key = `${school}##${dateStr}##${svc}`;

                if (!agg[key]) {
                    agg[key] = {
                        SchoolName: school,
                        Date: dateStr,
                        MealService: svc,
                        FreeCount: 0,
                        ReducedCount: 0,
                        PaidCount: 0,
                        TotalClaims: 0,
                        Notes: "",
                        ImageUrl: "",
                    };
                }

                const g = agg[key];
                const status = (rec.MealStatus || "").toUpperCase();
                const cnt = Number(rec.TotalMeals) || 0;
                if (status === "F") g.FreeCount += cnt;
                else if (status === "R") g.ReducedCount += cnt;
                else if (status === "P") g.PaidCount += cnt;
                g.TotalClaims += cnt;

                if (rec.Notes && !g.Notes) g.Notes = rec.Notes;
                if (rec.ImageUrl && !g.ImageUrl) g.ImageUrl = rec.ImageUrl;
            });

            const arr = Object.values(agg).sort((a, b) => {
                if (a.SchoolName < b.SchoolName) return -1;
                if (a.SchoolName > b.SchoolName) return 1;
                if (a.Date < b.Date) return -1;
                if (a.Date > b.Date) return 1;
                if (a.MealService < b.MealService) return -1;
                if (a.MealService > b.MealService) return 1;
                return 0;
            });

            setAggregatedData(arr);
        } catch {
            setError("Error aggregating report data.");
        } finally {
            setLoading(false);
        }
    };

    // --- CSV export ---
    const handleDownloadCSV = () => {
        const headers = [
            "School Name",
            "Date",
            "Meal Service",
            "Free Count",
            "Reduced Count",
            "Paid Count",
            "Total Claims",
            "Notes",
            "ImageUrl",
        ];
        const rows = [headers.join(",")];
        groupedRows.forEach((r) => {
            rows.push(
                [
                    `"${r.SchoolName}"`,
                    r.Date,
                    r.MealService,
                    r.FreeCount,
                    r.ReducedCount,
                    r.PaidCount,
                    r.TotalClaims,
                    r.Notes ? `"${r.Notes.replace(/"/g, '""')}"` : "",
                    r.ImageUrl ? `"${r.ImageUrl}"` : "",
                ].join(",")
            );
        });
        const blob = new Blob([rows.join("\n")], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "meal_report.csv";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    // --- Filter dropdown options ---
    const defaultMonths = ["2025-01", "2025-02", "2025-03", "2025-04"];
    const defaultMeals = ["Breakfast", "Lunch", "Supper", "Snack"];

    // months from aggregated data
    const uniqueMonths =
        aggregatedData.length > 0
            ? Array.from(new Set(aggregatedData.map((r) => r.Date.slice(0, 7)))).sort()
            : defaultMonths;

    // sites always from the raw view
    const uniqueSites = Array.from(
        new Set(mealData.map((r) => r.SchoolName).filter(Boolean))
    ).sort();

    // meal services from aggregated when available, else defaults
    const uniqueMealOptions =
        aggregatedData.length > 0
            ? Array.from(new Set(aggregatedData.map((r) => r.MealService))).sort()
            : defaultMeals;

    // --- Apply filters to aggregated data ---
    const filtered = aggregatedData.filter((r) => {
        const m = r.Date.slice(0, 7);
        return (
            (!filterMonth || m === filterMonth) &&
            (!filterSite || r.SchoolName === filterSite) &&
            (!filterMealService || r.MealService === filterMealService)
        );
    });

    // --- Group by site + totals row ---
    const groupedRows = [];
    let curr = null,
        buf = [],
        sum = { FreeCount: 0, ReducedCount: 0, PaidCount: 0, TotalClaims: 0 };

    const pushTotals = () => {
        groupedRows.push(...buf);
        groupedRows.push({
            SchoolName: curr,
            Date: "Totals",
            MealService: "",
            ...sum,
            isTotalRow: true,
            Notes: "",
            ImageUrl: "",
        });
    };

    filtered.forEach((r) => {
        if (r.SchoolName !== curr) {
            if (buf.length) pushTotals();
            curr = r.SchoolName;
            buf = [];
            sum = { FreeCount: 0, ReducedCount: 0, PaidCount: 0, TotalClaims: 0 };
        }
        buf.push(r);
        sum.FreeCount += r.FreeCount;
        sum.ReducedCount += r.ReducedCount;
        sum.PaidCount += r.PaidCount;
        sum.TotalClaims += r.TotalClaims;
    });
    if (buf.length) pushTotals();

    // --- Render ---
    return (
        <Box
            sx={{
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                p: 2,
                borderRadius: 1,
                width: "100%",
            }}
        >
            <Typography variant="h4" gutterBottom>
                Meal Report
            </Typography>

            {/* Filters */}
            <Box mb={2}>
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Select Month"
                            type="month"
                            fullWidth
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                "& .MuiInputBase-input": {
                                    color: theme.palette.text.primary,
                                },
                                "& .MuiInputLabel-root": {
                                    color: theme.palette.text.primary,
                                },
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            label="Select Site"
                            fullWidth
                            value={filterSite}
                            onChange={(e) => setFilterSite(e.target.value)}
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                "& .MuiInputBase-input": {
                                    color: theme.palette.text.primary,
                                },
                                "& .MuiInputLabel-root": {
                                    color: theme.palette.text.primary,
                                },
                            }}
                        >
                            <MenuItem value="">All Sites</MenuItem>
                            {uniqueSites.map((s) => (
                                <MenuItem key={s} value={s}>
                                    {s}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            select
                            label="Select Meal Service"
                            fullWidth
                            value={filterMealService}
                            onChange={(e) => setFilterMealService(e.target.value)}
                            sx={{
                                backgroundColor: theme.palette.background.paper,
                                "& .MuiInputBase-input": {
                                    color: theme.palette.text.primary,
                                },
                                "& .MuiInputLabel-root": {
                                    color: theme.palette.text.primary,
                                },
                            }}
                        >
                            <MenuItem value="">All Meal Services</MenuItem>
                            {uniqueMealOptions.map((m) => (
                                <MenuItem key={m} value={m}>
                                    {m}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
                <Box mt={2} display="flex" gap={2}>
                    <Button
                        variant="contained"
                        onClick={handleGenerateReport}
                        disabled={loading}
                    >
                        Generate Report
                    </Button>
                    <Button variant="contained" onClick={handleDownloadCSV}>
                        Download CSV
                    </Button>
                </Box>
            </Box>

            {loading && (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    minHeight="20vh"
                >
                    <CircularProgress />
                </Box>
            )}
            {error && (
                <Typography variant="body1" color="error" mb={2}>
                    {error}
                </Typography>
            )}
            {!loading && !error && aggregatedData.length === 0 && (
                <Typography variant="body1" mb={2}>
                    No data loaded. Click �Generate Report� to fetch report data.
                </Typography>
            )}

            {aggregatedData.length > 0 && !loading && !error && (
                <TableContainer
                    component={Paper}
                    sx={{
                        maxHeight: "70vh",
                        backgroundColor: theme.palette.background.paper,
                    }}
                >
                    <Table stickyHeader>
                        <TableHead>
                            <TableRow
                                sx={{
                                    backgroundColor: theme.palette.action.selected,
                                }}
                            >
                                {[
                                    "School Name",
                                    "Date",
                                    "Meal Service",
                                    "Free Count",
                                    "Reduced Count",
                                    "Paid Count",
                                    "Total Claims",
                                    "Notes",
                                    "Tic Sheet",
                                ].map((h) => (
                                    <TableCell
                                        key={h}
                                        align={
                                            ["Free Count", "Reduced Count", "Paid Count", "Total Claims"].includes(h)
                                                ? "right"
                                                : "left"
                                        }
                                        sx={{
                                            color: theme.palette.text.primary,
                                            fontWeight: "bold",
                                        }}
                                    >
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {groupedRows.map((row, i) => {
                                const isTot = row.isTotalRow;
                                const bg = isTot
                                    ? theme.palette.action.selected
                                    : i % 2 === 0
                                        ? theme.palette.action.hover
                                        : theme.palette.background.default;
                                return (
                                    <TableRow key={i} sx={{ backgroundColor: bg }}>
                                        <TableCell
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: isTot ? "bold" : "normal",
                                            }}
                                        >
                                            {row.SchoolName}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: isTot ? "bold" : "normal",
                                            }}
                                        >
                                            {row.Date}
                                        </TableCell>
                                        <TableCell
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: isTot ? "bold" : "normal",
                                            }}
                                        >
                                            {row.MealService}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: isTot ? "bold" : "normal",
                                            }}
                                        >
                                            {row.FreeCount}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: isTot ? "bold" : "normal",
                                            }}
                                        >
                                            {row.ReducedCount}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: isTot ? "bold" : "normal",
                                            }}
                                        >
                                            {row.PaidCount}
                                        </TableCell>
                                        <TableCell
                                            align="right"
                                            sx={{
                                                color: theme.palette.text.primary,
                                                fontWeight: isTot ? "bold" : "normal",
                                            }}
                                        >
                                            {row.TotalClaims}
                                        </TableCell>
                                        <TableCell sx={{ color: theme.palette.text.primary }}>
                                            {!isTot && row.Notes}
                                        </TableCell>
                                        <TableCell sx={{ color: theme.palette.text.primary }}>
                                            {!isTot && row.ImageUrl && (
                                                <Button
                                                    variant="outlined"
                                                    onClick={() => window.open(row.ImageUrl, "_blank")}
                                                >
                                                    View Tic Sheet
                                                </Button>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Box>
    );
};

export default EditCheck;
